/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { api } from "../lib/api";
import { postOrQueue } from "../lib/offline";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { HabitsTab } from "../components/HabitsTab";
import { VitalsTab } from "../components/VitalsTab";
import { Button } from "../components/ui/button";
import { Minus, Plus, HeartPulse, Pill, Smile, AlertCircle, CheckCircle2, Frown, Meh, Laugh, Angry, Annoyed } from "lucide-react";

const MOOD_FACES = [
  { score: 1, Icon: Angry, color: "#D9534F", label: "Très mal" },
  { score: 2, Icon: Frown, color: "#E68A4E", label: "Mal" },
  { score: 3, Icon: Meh, color: "#D4AF5A", label: "Moyen" },
  { score: 4, Icon: Smile, color: "#8AB960", label: "Bien" },
  { score: 5, Icon: Laugh, color: "#5CB85C", label: "Très bien" },
];

function classifyBP(sys, dia, patient) {
  const sysMax = patient?.cible_tension_sys_max ?? 140;
  const diaMax = patient?.cible_tension_dia_max ?? 90;
  if (sys >= sysMax + 20 || dia >= diaMax + 10) return { key: "high", label: "Élevée", color: "#D9534F" };
  if (sys >= sysMax || dia >= diaMax) return { key: "watch", label: "À surveiller", color: "#D4AF5A" };
  return { key: "in", label: "Dans la cible", color: "#5CB85C" };
}

function Counter({ value, setValue, min, max, testid }) {
  return (
    <div className="flex items-center gap-3">
      <button
        data-testid={`${testid}-minus`}
        onClick={() => setValue(Math.max(min, value - 1))}
        className="w-14 h-14 grid place-items-center rounded-full bg-white border border-[#E4E0D5] text-[#0A1426]"
      >
        <Minus size={24} />
      </button>
      <div data-testid={testid} className="font-[Fraunces] text-5xl font-bold text-[#0A1426] min-w-[100px] text-center">
        {value}
      </div>
      <button
        data-testid={`${testid}-plus`}
        onClick={() => setValue(Math.min(max, value + 1))}
        className="w-14 h-14 grid place-items-center rounded-full bg-white border border-[#E4E0D5] text-[#0A1426]"
      >
        <Plus size={24} />
      </button>
    </div>
  );
}

function TensionTab({ patient }) {
  const [sys, setSys] = useState(130);
  const [dia, setDia] = useState(82);
  const [readings, setReadings] = useState([]);

  const load = () => {
    if (!patient) return;
    api.get(`/bp-readings?patient_id=${patient.id}`)
      .then(({ data }) => setReadings(data))
      .catch(() => {});
  };
  useEffect(load, [patient]);

  const cls = classifyBP(sys, dia, patient);

  const submit = async () => {
    if (!patient) return;
    const r = await postOrQueue("/bp-readings", {
      patient_id: patient.id, systolique: sys, diastolique: dia,
      source: "app", client_datetime: new Date().toISOString(),
    });
    if (r._queued) toast.info("Enregistré hors-ligne. Sera envoyé au retour du réseau.");
    else toast.success("Mesure enregistrée");
    load();
  };

  const last10 = useMemo(() => readings.slice(0, 10).reverse(), [readings]);
  const maxSys = Math.max(160, ...last10.map((r) => r.systolique));
  const minSys = Math.min(100, ...last10.map((r) => r.systolique));

  return (
    <div className="flex flex-col gap-4 pt-2">
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
        <div className="flex items-center gap-2 mb-3">
          <HeartPulse size={20} className="text-[#2BA3A0]" />
          <h3 className="font-[Fraunces] text-xl font-bold text-[#0A1426]">Mesure du jour</h3>
        </div>
        <div className="grid grid-cols-2 gap-3 text-center">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold mb-2">Systolique</div>
            <Counter value={sys} setValue={setSys} min={70} max={240} testid="bp-sys" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold mb-2">Diastolique</div>
            <Counter value={dia} setValue={setDia} min={40} max={150} testid="bp-dia" />
          </div>
        </div>
        <div
          data-testid="bp-classification"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold"
          style={{ backgroundColor: `${cls.color}1f`, color: cls.color }}
        >
          {cls.key === "in" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {cls.label}
        </div>
        <p className="text-[11px] text-[#0A1426]/60 mt-3 leading-snug">
          Cible fixée avec votre médecin : ≤ {patient?.cible_tension_sys_max ?? 140}/{patient?.cible_tension_dia_max ?? 90}. Ceci n'est pas un avis médical.
        </p>
        <Button
          data-testid="bp-save-btn"
          onClick={submit}
          className="w-full h-14 mt-4 rounded-full bg-[#2BA3A0] hover:bg-[#0A1426] text-white font-bold text-base"
        >
          Enregistrer la mesure
        </Button>
      </div>

      {last10.length > 0 && (
        <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
          <div className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold mb-3">
            Vos {last10.length} dernières mesures
          </div>
          <svg viewBox={`0 0 ${last10.length * 30} 80`} className="w-full h-24">
            <polyline
              points={last10.map((r, i) => {
                const x = i * 30 + 8;
                const y = 80 - ((r.systolique - minSys) / Math.max(1, maxSys - minSys)) * 60 - 8;
                return `${x},${y}`;
              }).join(" ")}
              fill="none" stroke="#2BA3A0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            />
            {last10.map((r, i) => {
              const x = i * 30 + 8;
              const y = 80 - ((r.systolique - minSys) / Math.max(1, maxSys - minSys)) * 60 - 8;
              return <circle key={r.id} cx={x} cy={y} r="3.5" fill="#2BA3A0" />;
            })}
          </svg>
          <div className="text-[11px] text-[#0A1426]/60 mt-1">Tendance systolique</div>
        </div>
      )}
    </div>
  );
}

function MedsTab({ patient }) {
  const [meds, setMeds] = useState([]);
  const [logs, setLogs] = useState([]);
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    if (!patient) return;
    const [m, l] = await Promise.all([
      api.get(`/medications?patient_id=${patient.id}`),
      api.get(`/medication-logs?patient_id=${patient.id}&date=${today}`),
    ]);
    setMeds(m.data); setLogs(l.data);
  };
  useEffect(() => { load(); }, [patient]); // eslint-disable-line

  const isPris = (medId, moment) => logs.some((x) => x.medication_id === medId && x.moment === moment && x.pris);

  const totalMoments = meds.reduce((a, m) => a + m.moments.length, 0);
  const takenCount = logs.filter((l) => l.pris).length;
  const adherence = totalMoments ? Math.round((takenCount / totalMoments) * 100) : 0;

  const togglePris = async (med, moment) => {
    const wasPris = isPris(med.id, moment);
    await postOrQueue("/medication-logs", {
      patient_id: patient.id, medication_id: med.id, moment, date: today, pris: !wasPris,
    });
    load();
  };

  const MOMENT_LABEL = { matin: "Matin", midi: "Midi", soir: "Soir" };

  return (
    <div className="flex flex-col gap-4 pt-2">
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Observance aujourd'hui</div>
            <div className="font-[Fraunces] text-4xl font-bold text-[#0A1426] mt-1" data-testid="adherence-percent">
              {adherence}%
            </div>
          </div>
          <Pill size={42} className="text-[#2BA3A0]" />
        </div>
        <div className="text-[12px] text-[#0A1426]/60">{takenCount} prise(s) sur {totalMoments}</div>
      </div>

      <div className="flex flex-col gap-3">
        {meds.map((med) => (
          <div key={med.id} data-testid={`med-${med.nom}`} className="bg-white rounded-2xl border border-[#E4E0D5] p-4">
            <div className="flex items-baseline justify-between">
              <div className="font-[Fraunces] text-lg font-bold text-[#0A1426]">{med.nom}</div>
              <div className="text-[12px] text-[#0A1426]/60 font-medium">{med.dose}</div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {med.moments.map((mo) => {
                const pris = isPris(med.id, mo);
                return (
                  <button
                    key={mo}
                    data-testid={`med-${med.nom}-${mo}`}
                    onClick={() => togglePris(med, mo)}
                    className={`px-4 py-2.5 rounded-full text-sm font-bold flex items-center gap-1.5 transition-colors ${
                      pris ? "bg-[#5CB85C] text-white" : "bg-[#F5F3EE] text-[#0A1426]"
                    }`}
                  >
                    {pris && <CheckCircle2 size={16} />}
                    {MOMENT_LABEL[mo]}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MoodTab({ patient }) {
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => {
    if (!patient) return;
    api.get(`/mood-checkins?patient_id=${patient.id}`)
      .then(({ data }) => setHistory(data));
  };
  useEffect(load, [patient]);

  const lowStreak = history.slice(0, 3).filter((h) => h.score <= 2).length >= 2;

  const submit = async (score) => {
    setSelected(score);
    await postOrQueue("/mood-checkins", {
      patient_id: patient.id, score, client_date: new Date().toISOString(),
    });
    toast.success("Merci d'avoir partagé.");
    load();
  };

  return (
    <div className="flex flex-col gap-4 pt-2">
      <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
        <div className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Aujourd'hui</div>
        <div className="font-[Fraunces] text-2xl font-bold text-[#0A1426] mt-1">Comment je vais ?</div>
        <div className="flex items-center justify-between mt-5">
          {MOOD_FACES.map(({ score, Icon, color, label }) => (
            <button
              key={score}
              data-testid={`mood-face-${score}`}
              onClick={() => submit(score)}
              className={`flex flex-col items-center gap-1 ${selected === score ? "mood-active" : ""}`}
            >
              <span
                className="w-14 h-14 grid place-items-center rounded-full"
                style={{
                  backgroundColor: `${color}1f`,
                  color,
                  outline: selected === score ? `2px solid ${color}` : "none",
                  outlineOffset: "3px",
                }}
              >
                <Icon size={32} strokeWidth={2.2} />
              </span>
              <span className="text-[10px] font-bold text-[#0A1426]/70">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {lowStreak && (
        <div data-testid="mood-low-alert" className="bg-[#D4AF5A]/15 border border-[#D4AF5A]/40 rounded-2xl p-4">
          <div className="flex items-center gap-2 font-bold text-[#8A6D2B]">
            <Annoyed size={20} /> On est avec vous.
          </div>
          <p className="text-[13px] text-[#0A1426]/80 mt-1 leading-snug">
            Les baisses de moral sont fréquentes après un AVC. Ce n'est pas une faiblesse.
          </p>
          <button
            data-testid="alert-team-btn"
            className="mt-3 w-full h-12 rounded-full bg-[#0A1426] text-white font-bold"
          >
            Prévenir mon équipe de soin
          </button>
        </div>
      )}
    </div>
  );
}

export default function HealthPage() {
  const { patient } = useApp();
  const loc = useLocation();
  const initialTab = loc.state?.tab || "bp";

  if (!patient) {
    return <div className="pt-10 text-center text-[#0A1426]/70">Aucun patient lié.</div>;
  }

  return (
    <div className="bc-in pt-2 pb-6">
      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="flex flex-wrap w-full h-auto gap-1 bg-white rounded-3xl p-1.5 border border-[#E4E0D5] justify-start">
          <TabsTrigger data-testid="tab-bp" value="bp" className="flex-1 min-w-fit h-10 px-3 rounded-full data-[state=active]:bg-[#2BA3A0] data-[state=active]:text-white font-bold text-[12px]">
            Tension
          </TabsTrigger>
          <TabsTrigger data-testid="tab-vitals" value="vitals" className="flex-1 min-w-fit h-10 px-3 rounded-full data-[state=active]:bg-[#2BA3A0] data-[state=active]:text-white font-bold text-[12px]">
            Poids & Glycémie
          </TabsTrigger>
          <TabsTrigger data-testid="tab-meds" value="meds" className="flex-1 min-w-fit h-10 px-3 rounded-full data-[state=active]:bg-[#2BA3A0] data-[state=active]:text-white font-bold text-[12px]">
            Médicaments
          </TabsTrigger>
          <TabsTrigger data-testid="tab-mood" value="mood" className="flex-1 min-w-fit h-10 px-3 rounded-full data-[state=active]:bg-[#2BA3A0] data-[state=active]:text-white font-bold text-[12px]">
            Humeur
          </TabsTrigger>
          <TabsTrigger data-testid="tab-habits" value="habits" className="flex-1 min-w-fit h-10 px-3 rounded-full data-[state=active]:bg-[#2BA3A0] data-[state=active]:text-white font-bold text-[12px]">
            Habitudes
          </TabsTrigger>
        </TabsList>
        <TabsContent value="bp"><TensionTab patient={patient} /></TabsContent>
        <TabsContent value="vitals"><VitalsTab /></TabsContent>
        <TabsContent value="meds"><MedsTab patient={patient} /></TabsContent>
        <TabsContent value="mood"><MoodTab patient={patient} /></TabsContent>
        <TabsContent value="habits"><HabitsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
